<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="paymentsTableData"
      :columns="paymentsTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: ['payment_number'],
          right: props.isDashboard ? [] : ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchPayments"
    >
      <template #actions>
        <PaymentsTableBulkActions
          :selectedPayments="selectedPayments"
          v-if="selectedPayments.length >= 1"
          @reloadTable="getPaymentsTableList"
          @clearSelection="handleClearSelection"
        />
      </template>
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { ref, h } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePaymentStore } from '@/stores/payment.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';

import PaymentsTableRowActions from './PaymentsTableRowActions.vue';
import PaymentsTableBulkActions from './PaymentsTableBulkActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';
import { GetStatusRenderer, GetRelatedOrdersRenderer } from './Renderers';

const fancyTableRef = ref<any>(null);
const selectedPayments = ref<any[]>([]);
// Cache to store all selected row data across pages
const selectedRowsCache = ref<Map<string, any>>(new Map());
const { GetFieldRenderer } = useTableRenderers();

const { getPayments } = usePaymentStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
  isDashboard?: boolean;
}>();

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'payment_number':
        if (value) params.payment_number = value;
        break;
      case 'supplier_name':
        if (value) params.supplier_name = value;
        break;
      case 'invoice_number':
        if (value) params.invoice_number = value;
        break;
      case 'internal_order_number':
        if (value) params.internal_order_number = value;
        break;
      case 'due_date':
        if (value?.from) params.due_date_after = value.from;
        if (value?.to) params.due_date_before = value.to;
        break;
      case 'invoice_date':
        if (value?.from) params.invoice_date_after = value.from;
        if (value?.to) params.invoice_date_before = value.to;
        break;
      case 'service_date':
        if (value?.from) params.service_date_after = value.from;
        if (value?.to) params.service_date_before = value.to;
        break;
      case 'amount':
        if (value?.start !== undefined) params.amount_min = value.start;
        if (value?.end !== undefined) params.amount_max = value.end;
        break;
      case 'created_on':
        if (value?.from) params.created_after = value.from;
        if (value?.to) params.created_before = value.to;
        break;
      case 'created_by_name':
        if (value) params.created_by = value;
        break;
      case 'approved_by_name':
        if (value) params.approved_by_name = value;
        break;
      case 'rejected_by_name':
        if (value) params.rejected_by_name = value;
        break;
      case 'cleared_by_names':
        if (value) params.cleared_by_name = value;
        break;
      case 'payed':
        if (value !== undefined) {
          params.payed = value ? 'true' : 'false';
        }
        break;
      case 'status_display':
        if (value && value.length > 0) params.status = value.join(',');
        break;
      case 'is_clarification_requested':
        if (value !== undefined) {
          params.clarification_requested_only = value ? 'true' : 'false';
        }
        break;
      case 'orders':
        if (value) params.order_number = value;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  switch (id) {
    case 'payment_number':
      backendField = 'payment_number_without_prefix';
      break;
    case 'supplier_name':
      backendField = 'supplier_name_sort';
      break;
    case 'invoice_number':
      backendField = 'invoice_number';
      break;
    case 'internal_order_number':
      backendField = 'internal_order_number';
      break;
    case 'due_date':
      backendField = 'due_date';
      break;
    case 'invoice_date':
      backendField = 'invoice_date';
      break;
    case 'service_date':
      backendField = 'service_date';
      break;
    case 'amount':
      backendField = 'amount_sort';
      break;
    case 'status_display':
      backendField = 'status_display';
      break;
    case 'created_by_name':
      backendField = 'created_by_name_sort';
      break;
    case 'approved_by_name':
    case 'rejected_by_name':
      backendField = 'released_by_name_sort';
      break;
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchPayments = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer: props.customerId,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getPayments(apiParams);

    paymentsTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('[FETCH] Error fetching payments:', error);
    paymentsTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getPaymentsTableList = async () => {
  await fetchPayments({
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    sorting: fancyTableRef.value?.getSerializedState()?.sorting || [],
    filters: fancyTableRef.value?.getSerializedState()?.columnFilters || [],
  });
};

const handleClearSelection = () => {
  fancyTableRef.value.resetRowSelection();
  selectedRowsCache.value.clear();
  selectedPayments.value = [];
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: props.isDashboard ? 'payments_dashboard' : 'payments',
  fancyTableRef,
  tableLoader: async () => {
    await fetchPayments({
      pageIndex: 0,
      pageSize: currentSavedState.value?.pageSize || 10,
      sorting: currentSavedState.value?.sorting || [],
      filters: currentSavedState.value?.columnFilters || [],
    });
  },
});

const isLoading = ref(false);
const paymentsTableData = ref<any[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const paymentStatusOptions = [
  {
    label: t('orders.titles.rejected'),
    value: 'REJECTED',
    class:
      'tw-bg-red-100 tw-border-red-100 tw-text-red-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.approved'),
    value: 'APPROVED',
    class:
      'tw-bg-green-100 tw-border-green-100 tw-text-green-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('requests.titles.pending'),
    value: 'PENDING_APPROVAL',
    class:
      'tw-bg-amber-100 tw-border-amber-100 tw-text-amber-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.draft'),
    value: 'DRAFT',
    class:
      'tw-bg-gray-100 tw-border-gray-100 tw-text-gray-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.clearing'),
    value: 'CLEARING',
    class:
      'tw-bg-purple-100 tw-border-purple-100 tw-text-purple-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
];

const baseHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('payment.titles.payment_no'),
    accessorKey: 'payment_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.LINK_INTERNAL,
    cell: GetFieldRenderer(FieldType.LINK_INTERNAL, {
      link: {
        name: 'payment',
        key: 'id',
      },
    }),
    size: 150,
  },
  {
    header: t('orders.titles.supplier'),
    accessorKey: 'supplier_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    cell: info => {
      const name = info.getValue();
      if (!name) return '-';
      const isDeleted = info.row.original.supplier_is_deleted;
      if (isDeleted) {
        return h(
          'span',
          { class: 'tw-text-secondary-300 tw-cursor-default' },
          `${name} (${t('suppliers.labels.deleted')})`
        );
      }
      const supplierId = info.row.original.supplier_id;
      if (!supplierId) return name;
      return h(
        RouterLink,
        {
          class: 'tw-underline tw-text-secondary-500 hover:tw-text-primary-600 tw-cursor-pointer',
          to: { name: 'edit-supplier', params: { supplierId } },
        },
        () => name
      );
    },
    size: 150,
  },
  {
    header: t('payment.titles.invoice_no'),
    accessorKey: 'invoice_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('payment.titles.internal_order_no'),
    accessorKey: 'internal_order_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('payment.titles.due_date'),
    accessorKey: 'due_date',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 120,
  },
  {
    header: t('payment.titles.invoice_date'),
    accessorKey: 'invoice_date',
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.service_date'),
    accessorKey: 'service_date',
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('payment.titles.payment_amount'),
    accessorKey: 'amount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    size: 120,
  },
  {
    header: t('orders.titles.created_on'),
    accessorKey: 'created_on',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 120,
  },
  {
    header: t('orders.titles.created_by'),
    accessorKey: 'created_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('orders.titles.approved_by'),
    accessorKey: 'approved_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('orders.titles.rejected_by'),
    accessorKey: 'rejected_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 120,
  },
  {
    header: t('orders.titles.cleared_by'),
    accessorKey: 'cleared_by_names',
    enableSorting: false,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: (info: any) => {
      const names: string[] = info.getValue() ?? [];
      if (!names.length) return '-';
      const joined = names.join(', ');
      return h('span', { title: joined, class: 'tw-truncate tw-block tw-max-w-full' }, joined);
    },
    size: 200,
  },
  {
    header: t('payment.titles.paid'),
    accessorKey: 'payed',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 80,
  },
  {
    header: t('orders.titles.status'),
    accessorKey: 'status_display',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BADGE,
    filterType: FilterType.MULTI_SELECT,
    filterFn: 'enumFilter',
    cell: GetStatusRenderer(paymentStatusOptions),
    filterOptions: paymentStatusOptions,
    size: 150,
  },
  {
    header: t('orders.titles.clarification_requested'),
    accessorKey: 'is_clarification_requested',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('orders.titles.related_orders'),
    accessorKey: 'orders',
    enableSorting: false,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.ARRAY,
    cell: GetRelatedOrdersRenderer(),
    filterType: FilterType.TEXT,
    filterFn: 'ordersArray',
    size: 200,
  },
];

const actionsColumn: FancyTableColumnDef<any> = {
  header: t('common.headers.actions'),
  accessorKey: 'actions',
  enableSorting: false,
  enableColumnFilter: false,
  enableHiding: false,
  hideFromColumnConfig: true,
  fieldType: FieldType.TEXT,
  cell: info =>
    h(PaymentsTableRowActions, {
      key: `${info.row.original.id}-${info.row.original.status_display}-${info.row.original.is_clarification_requested}-${info.row.original.payed}`,
      payment: info.row.original,
      onReloadTable: getPaymentsTableList,
    }),
  size: 100,
};

const paymentsTableHeaders: FancyTableColumnDef<any>[] = props.isDashboard
  ? baseHeaders
  : [...baseHeaders, actionsColumn];

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(paymentsTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Update selectedPayments with all cached rows
  selectedPayments.value = Array.from(selectedRowsCache.value.values());
};
</script>
